export interface AssignableEcosystem {
  id: string;
  displayName: string;
  memberGroup: string;
}

export interface EcosystemAttachResult {
  ecosystemId: string;
  memberGroup: string;
  status: string;
  code?: string;
}

export interface CreateUserPayload {
  username: string;
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  ecosystemIds?: string[];
}

export interface CreateUserResult {
  userId: string;
  username: string;
  email: string;
  passwordTemporary: boolean;
  ecosystemAttach: EcosystemAttachResult[];
  openProjectNote?: string;
  idempotencyKey: string;
  idempotentReplay?: boolean;
}

export interface InviteUserPayload {
  username: string;
  email: string;
  firstName?: string;
  lastName?: string;
  emailVerified?: boolean;
}

export interface InviteUserResult {
  userId: string;
  username: string;
  email: string;
  created: boolean;
  inviteSent: boolean;
  requiredActions: string[];
  idempotencyKey: string;
  idempotentReplay?: boolean;
}

export interface UserLookupResult {
  userId: string;
  username: string;
  email: string;
}

export interface ProjectionStepResult {
  service: string;
  status: string;
  code?: string;
  reason?: string;
  detail?: Record<string, unknown>;
}

export interface EcosystemMembershipResult {
  userId: string;
  ecosystemId: string;
  memberGroup: string;
  status: string;
  projection: ProjectionStepResult[];
  idempotencyKey?: string;
  idempotentReplay?: boolean;
}

function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

async function adminUserFetch<T>(
  token: string,
  method: string,
  path: string,
  body?: unknown,
  idempotencyKey?: string
): Promise<T> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
  if (idempotencyKey) {
    headers['Idempotency-Key'] = idempotencyKey;
  }

  const res = await fetch(path, {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  const rawText = await res.text();
  let data: Record<string, unknown> = {};
  if (rawText) {
    try {
      data = JSON.parse(rawText) as Record<string, unknown>;
    } catch {
      data = {};
    }
  }
  if (!res.ok) {
    const message =
      (typeof data.message === 'string' && data.message) ||
      (rawText && !rawText.trimStart().startsWith('<')
        ? rawText.trim().slice(0, 180)
        : '') ||
      `Request failed: ${res.status} ${method} ${path}`;
    const err = new Error(message);
    Object.assign(err, {
      status: res.status,
      code: data.code,
      existingUserId: data.existingUserId,
      hint: data.hint,
    });
    throw err;
  }
  return data as T;
}

export async function fetchAssignableEcosystems(token: string): Promise<AssignableEcosystem[]> {
  const data = await adminUserFetch<{ ecosystems: AssignableEcosystem[] }>(
    token,
    'GET',
    '/api/admin/users/assignable-ecosystems'
  );
  return data.ecosystems || [];
}

export async function adminCreateRealmUser(
  token: string,
  payload: CreateUserPayload,
  idempotencyKey = newIdempotencyKey()
): Promise<CreateUserResult> {
  return adminUserFetch<CreateUserResult>(
    token,
    'POST',
    '/api/admin/users/create',
    payload,
    idempotencyKey
  );
}

export async function adminInviteRealmUser(
  token: string,
  payload: InviteUserPayload,
  idempotencyKey = newIdempotencyKey()
): Promise<InviteUserResult> {
  return adminUserFetch<InviteUserResult>(
    token,
    'POST',
    '/api/admin/users/invite',
    payload,
    idempotencyKey
  );
}

/** Resolve email → Keycloak userId (email stays in JSON body, not URL paths). */
export async function adminLookupUserByEmail(
  token: string,
  email: string
): Promise<UserLookupResult> {
  return adminUserFetch<UserLookupResult>(token, 'POST', '/api/admin/users/lookup', { email });
}

export interface RealmUserSearchResult {
  userId: string;
  username: string;
  email: string;
  firstName?: string;
  lastName?: string;
  enabled?: boolean;
  emailVerified?: boolean;
}

export interface UpdateRealmUserPayload {
  firstName?: string;
  lastName?: string;
  email?: string;
  enabled?: boolean;
  emailVerified?: boolean;
}

/** Typeahead search against Keycloak Admin users (username / email / name). */
export async function adminSearchRealmUsers(
  token: string,
  q: string,
  max = 25,
  first = 0
): Promise<RealmUserSearchResult[]> {
  const params = new URLSearchParams();
  if (q.trim()) params.set('q', q.trim());
  params.set('max', String(max));
  if (first > 0) params.set('first', String(first));
  const data = await adminUserFetch<{ users: RealmUserSearchResult[] }>(
    token,
    'GET',
    `/api/admin/users/search?${params.toString()}`
  );
  return data.users || [];
}

export async function adminUpdateRealmUser(
  token: string,
  userId: string,
  payload: UpdateRealmUserPayload
): Promise<RealmUserSearchResult> {
  return adminUserFetch<RealmUserSearchResult>(
    token,
    'PATCH',
    `/api/admin/users/${encodeURIComponent(userId)}`,
    payload
  );
}

export async function adminAttachUserEcosystem(
  token: string,
  userId: string,
  ecosystemId: string,
  idempotencyKey = newIdempotencyKey()
): Promise<EcosystemMembershipResult> {
  return adminUserFetch<EcosystemMembershipResult>(
    token,
    'POST',
    `/api/admin/users/${encodeURIComponent(userId)}/ecosystems/${encodeURIComponent(ecosystemId)}/attach`,
    undefined,
    idempotencyKey
  );
}

export async function adminDetachUserEcosystem(
  token: string,
  userId: string,
  ecosystemId: string,
  idempotencyKey = newIdempotencyKey()
): Promise<EcosystemMembershipResult> {
  return adminUserFetch<EcosystemMembershipResult>(
    token,
    'DELETE',
    `/api/admin/users/${encodeURIComponent(userId)}/ecosystems/${encodeURIComponent(ecosystemId)}/detach`,
    undefined,
    idempotencyKey
  );
}

export function generateTemporaryPassword(length = 14): string {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghijkmnopqrstuvwxyz';
  const digits = '23456789';
  const special = '!@#$%&*';
  const all = upper + lower + digits + special;
  const pick = (chars: string) => chars[Math.floor(Math.random() * chars.length)];
  const base = [pick(upper), pick(lower), pick(digits), pick(special)];
  while (base.length < length) {
    base.push(pick(all));
  }
  for (let i = base.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [base[i], base[j]] = [base[j], base[i]];
  }
  return base.join('');
}
